import { lighten, styled, Toolbar, Tooltip, Typography } from "@mui/material";
import clsx from "clsx";
import { StyledButton } from "../../components/StyledButton";

const PREFIX = "GroupTableToolbar";

const classes = {
	root: `${PREFIX}-root`,
	highlight: `${PREFIX}-highlight`,
	title: `${PREFIX}-title`,
	download: `${PREFIX}-download`,
};

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
	[`&.${classes.root}`]: {
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(1),
	},

	[`&.${classes.highlight}`]:
		theme.palette.mode === "light"
			? {
					color: theme.palette.secondary.main,
					backgroundColor: lighten(theme.palette.secondary.light, 0.85),
			  }
			: {
					color: theme.palette.text.primary,
					backgroundColor: theme.palette.secondary.dark,
			  },

	[`& .${classes.title}`]: {
		flex: "1 1 100%",
	},

	[`& .${classes.download}`]: {
		minWidth: 100,
		marginRight: theme.spacing(1),
	},
}));

export interface GroupTableToolbarProps {
	/**
	 * Additional toolbar buttons.
	 */
	children?: JSX.Element | JSX.Element[];
	numSelected?: number;
	handleDownload?: () => void;
	title: string;
}

export const GroupTableToolbar = ({
	children,
	numSelected,
	handleDownload,
	title,
}: GroupTableToolbarProps): JSX.Element => {
	if (numSelected === undefined) {
		return (
			<StyledToolbar
				className={clsx(classes.root, {
					[classes.highlight]: false,
				})}
			>
				<Typography
					className={classes.title}
					variant="h6"
					id="tableTitle"
					component="div"
				>
					{title}
				</Typography>

				{children}
			</StyledToolbar>
		);
	} else {
		return (
			<StyledToolbar
				className={clsx(classes.root, {
					[classes.highlight]: numSelected > 0,
				})}
			>
				{numSelected > 0 ? (
					<>
						<Typography
							className={classes.title}
							color="inherit"
							variant="subtitle1"
							component="div"
						>
							{numSelected} selected
						</Typography>
						<Tooltip title="Download">
							<StyledButton
								data-testid="download-button"
								onClick={handleDownload}
								aria-label="download"
								size="medium"
								color="primary"
								variant="contained"
								className={classes.download}
							>
								Download
							</StyledButton>
						</Tooltip>
					</>
				) : (
					<>
						<Typography
							className={classes.title}
							variant="h6"
							id="tableTitle"
							component="div"
						>
							{title}
						</Typography>
					</>
				)}
				{children}
			</StyledToolbar>
		);
	}
};
